import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Alert, Box, Button, Grid, InputAdornment, Paper, Stack, Typography } from "@mui/material";
import { FormikProps } from "formik";

import { FormikDecimalTextField } from "../../../../../../../../formik/FormikDecimalTextField";
import { FormikTextField } from "../../../../../../../../formik/FormikTextField";
import { FormikYesNoAutocomplete } from "../../../../../../../../formik/FormikYesNoAutocomplete";
import { FormikAutocomplete } from "../../../../../../../../formik/formik-autocomplete/FormikAutocomplete";
import { IOptionItem } from "../../../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "../../../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "../../../../../../../../formik/formik-date-picker/FormikDatePicker";
import { FormikZipCodeField } from "../../../../../../../../formik/formik-zipcode-field/FormikZipCodeField";
import { useDropdownOptions } from "../../../../../../../../hooks/useDropdownOptions";
import {
  AssetEncumbrance,
  AssetFinancialHoldingKind,
  ProceedingParticipantSearchResult,
} from "../../../../../../../api/ClientFileApi";
import { FieldSpacer } from "../../../../../../components/field-spacer/FieldSpacer";
import { ParticipantSearchSelect } from "../../../../../../components/participant-search-select/ParticipantSearchSelect";
import { AssetsFormValues, AssetsMaxLength, FinancialHoldingField } from "../../../../AssetsView.types";
import { FinancialHoldingKindConfig } from "../../../../AssetsView.utils";

const TAB = "clients.client.debtSettlement.assetInventory.assetsTab";
const FULL = 12;
const HALF = { xs: 12, md: 6 };
const THIRD = { xs: 12, md: 4 };

interface Props {
  formik: FormikProps<AssetsFormValues>;
  index: number;
  number: number;
  config: FinancialHoldingKindConfig;
  claimOptions: IOptionItem[];
  onDelete: () => void;
}

export function FinancialHoldingBlock({ formik, index, number, config, claimOptions, onDelete }: Props) {
  const { t } = useTranslation();
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const { countries } = useDropdownOptions();

  const holding = formik.values.financialHoldings[index];
  if (!holding) {
    return null;
  }

  const kindKey = `${TAB}.holdings.${config.translationKey}` as const;
  const isFurther = number > 1;
  const path = (field: FinancialHoldingField) => `financialHoldings.${index}.${field}` as const;
  const fieldId = (field: FinancialHoldingField) => `assets-holding-${holding.key}-${field}`;
  const title = t(`${kindKey}.blockTitle`, { number });

  const encumbranceOptions: IOptionItem[] = [
    { id: AssetEncumbrance.Yes, label: t(`${TAB}.holding.encumbranceYes`) },
    { id: AssetEncumbrance.No, label: t(`${TAB}.holding.encumbranceNo`) },
    { id: AssetEncumbrance.Unknown, label: t(`${TAB}.holding.encumbranceUnknown`) },
  ];

  const selectPlaceholder = { placeholder: t(`${TAB}.pleaseSelect`) };

  const fillFromParticipant = (participant: ProceedingParticipantSearchResult | null) => {
    setSelectedParticipantId(participant?.proceedingParticipantId ?? null);
    if (!participant) {
      return;
    }
    void formik.setFieldValue(path("institutionName"), participant.name);
    void formik.setFieldValue(path("street"), participant.street ?? null);
    void formik.setFieldValue(path("houseNumber"), participant.houseNumber ?? null);
    void formik.setFieldValue(path("zipCode"), participant.zipCode ?? null);
    void formik.setFieldValue(path("city"), participant.city ?? null);
    void formik.setFieldValue(path("country"), participant.country ?? null);
  };

  const text = (field: FinancialHoldingField, label: string, maxLength: number) => (
    <FormikTextField
      id={fieldId(field)}
      formik={formik}
      path={path(field)}
      label={label}
      slotProps={{ input: { inputProps: { maxLength } } }}
    />
  );

  const warning = (text: string) => (
    <Box component="span" sx={{ color: "error.main" }}>
      {text}
    </Box>
  );

  const yesNo = (field: FinancialHoldingField, label: string, helperText?: ReactNode) => (
    <FormikYesNoAutocomplete
      id={fieldId(field)}
      formik={formik}
      path={path(field)}
      label={label}
      helperText={helperText}
      disableClearable
    />
  );

  return (
    <Paper variant="outlined" component="article" aria-label={title} sx={{ p: 2 }}>
      <Stack gap={2}>
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Grid container spacing={2}>
          {config.participantType && (
            <Grid size={FULL}>
              <ParticipantSearchSelect
                id={`${fieldId("institutionName")}-selection`}
                label={t(`${TAB}.holding.selection`)}
                type={config.participantType}
                selectedId={selectedParticipantId}
                helperText=" "
                onChange={fillFromParticipant}
              />
            </Grid>
          )}
          <Grid size={HALF}>
            {text(
              "institutionName",
              t(isFurther ? `${kindKey}.institutionFurther` : `${kindKey}.institution`),
              AssetsMaxLength.institutionName
            )}
          </Grid>
          <Grid size={HALF}>
            {text(
              "accountNumber",
              t(isFurther ? `${kindKey}.accountNumberFurther` : `${kindKey}.accountNumber`),
              AssetsMaxLength.accountNumber
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>{text("street", t(`${TAB}.holding.street`), AssetsMaxLength.street)}</Grid>
          <Grid size={THIRD}>{text("houseNumber", t(`${TAB}.holding.houseNumber`), AssetsMaxLength.houseNumber)}</Grid>
          <Grid size={THIRD}>
            <FormikZipCodeField
              id={fieldId("zipCode")}
              formik={formik}
              path={path("zipCode")}
              label={t(`${TAB}.holding.zipCode`)}
              slotProps={{ input: { inputProps: { maxLength: AssetsMaxLength.zipCode } } }}
            />
          </Grid>
          <Grid size={THIRD}>{text("city", t(`${TAB}.holding.city`), AssetsMaxLength.city)}</Grid>
          <Grid size={THIRD}>
            <FormikAutocomplete
              id={fieldId("country")}
              formik={formik}
              path={path("country")}
              label={t(`${TAB}.holding.country`)}
              options={countries}
              freeSolo
            />
          </Grid>

          <Grid size={FULL}>
            <FormikAutocompleteSelect
              id={fieldId("encumbrance")}
              formik={formik}
              path={path("encumbrance")}
              label={t(`${kindKey}.encumbrance`)}
              options={encumbranceOptions}
              disableClearable
              textFieldProps={selectPlaceholder}
              helperText={
                holding.encumbrance === AssetEncumbrance.Unknown
                  ? warning(t(`${TAB}.holding.encumbranceWarning`))
                  : undefined
              }
            />
          </Grid>
          {holding.encumbrance === AssetEncumbrance.Unknown && (
            <Grid size={FULL}>
              <Alert severity="info">{t(`${kindKey}.encumbranceInfo`)}</Alert>
              <FieldSpacer />
            </Grid>
          )}
          {holding.encumbrance === AssetEncumbrance.Yes && (
            <Grid size={FULL}>
              <FormikAutocompleteSelect
                id={fieldId("encumberedClaimId")}
                formik={formik}
                path={path("encumberedClaimId")}
                label={t(`${kindKey}.encumberedClaim`)}
                options={claimOptions}
                disableClearable
                textFieldProps={selectPlaceholder}
              />
            </Grid>
          )}
          {config.kind === AssetFinancialHoldingKind.SavingsAccount && holding.encumbrance === AssetEncumbrance.No && (
            <Grid size={FULL}>{yesNo("usedForProceedingCosts", t(`${TAB}.holding.usedForProceedingCosts`))}</Grid>
          )}

          <Grid size={FULL}>
            {yesNo(
              "isValueKnown",
              t(`${kindKey}.isValueKnown`),
              holding.isValueKnown === false ? warning(t(`${TAB}.holding.valueWarning`)) : undefined
            )}
          </Grid>
          {holding.isValueKnown === false && (
            <Grid size={FULL}>
              <Alert severity="info">{t(`${kindKey}.valueUnknownInfo`)}</Alert>
              <FieldSpacer />
            </Grid>
          )}
          {holding.isValueKnown === true && (
            <>
              <Grid size={HALF}>
                <FormikDecimalTextField
                  id={fieldId("value")}
                  formik={formik}
                  path={path("value")}
                  label={t(`${TAB}.holding.value`)}
                  decimalScale={2}
                  minValue={0}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
                />
              </Grid>
              <Grid size={HALF}>
                <FormikDatePicker formik={formik} path={path("valueDate")} label={t(`${TAB}.holding.valueDate`)} />
              </Grid>
            </>
          )}

          <Grid size={FULL}>
            <FormikTextField
              id={fieldId("remarks")}
              formik={formik}
              path={path("remarks")}
              label={t(`${TAB}.holding.remarks`)}
              multiline
              minRows={2}
              slotProps={{ input: { inputProps: { maxLength: AssetsMaxLength.remarks } } }}
            />
          </Grid>
        </Grid>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          aria-label={`${t(`${kindKey}.remove`)}: ${title}`}
          onClick={onDelete}
          sx={{ alignSelf: "flex-end" }}
        >
          {t(`${kindKey}.remove`)}
        </Button>
      </Stack>
    </Paper>
  );
}
